import { useEffect, useState } from "react";
import { useMoveNote, useSpaceTree } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { Note } from "@/api/types";
import { useToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TreePicker } from "./TreePicker";

export function MoveNoteDialog({ note, open, onOpenChange }: { note: Note; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [parentId, setParentId] = useState<string | null>(note.parentId ?? null);
  const tree = useSpaceTree(note.spaceId, open);
  const move = useMoveNote(note.id);
  const { toast } = useToast();

  useEffect(() => {
    if (open) setParentId(note.parentId ?? null);
  }, [open, note.parentId]);

  const unchanged = parentId === (note.parentId ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move “{note.title || "Untitled"}”</DialogTitle>
          <DialogDescription>Pick a new parent page, or top level. Pages inside move with it.</DialogDescription>
        </DialogHeader>
        <TreePicker tree={tree.data} loading={tree.isLoading} value={parentId} onChange={setParentId} excludeId={note.id} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={unchanged || move.isPending}
            onClick={() =>
              move.mutate(
                { parentId },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    toast({ title: "Note moved", tone: "success" });
                  },
                  onError: (err) => toast({ title: "Could not move note", description: errorMessage(err), tone: "error" }),
                },
              )
            }
          >
            {move.isPending ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
